<template>
  <div>
    <div ref="chartdiv" class="line-chart" :key="componentKey"></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import amThemeAnimated from '@amcharts/amcharts4/themes/animated'
import timeDuration from '@/modules/time-duration'
import getAverageOfArray from '@/modules/average-of-array.js'

import('@/styles/components/chart.less')
const DEFAULT_DURATION_SEC = 1200

export default {
  name: 'TrainingChart',
  components: {},
  props: {
    trainingType: {
      type: Number,
      required: true
    },
    rhythmogram: {
      type: Object,
      required: true
    },
    min: {
      type: Number,
      required: false,
      default: null
    },
    max: {
      type: Number,
      required: false,
      default: null
    },
    events: {
      type: Array,
      required: false
    },
    isActiveTraining: {
      type: Boolean,
      default: false
    },
    trainingDuration: {
      type: Number,
      required: false
    },
    steps: {
      type: Object,
      required: false
    },
    breath: {
      type: Object,
      required: false
    },
    patientMaxHr: {
      type: Number,
      required: false
    },
    ecgRange: {
      type: Object,
      required: false
    },
    dateStart: {
      type: Date,
      required: true
    }
  },
  data: () => ({
    minHRValue: 50,
    maxHRValue: 120,
    minBreathValue: 10,
    maxBreathValue: 40,
    duration: DEFAULT_DURATION_SEC,
    chartData: [],
    breathData: [],
    stepsData: [],
    axisRanges: {},
    cursorPosition: {},
    componentKey: 0,
    isTimeMode: false,
    currentZoomState: null
  }),
  computed: {
    hasEvents() {
      return this.events && this.events.length > 0
    },
    chartDataLast() {
      return this.chartData[this.chartData.length - 1]
    },
    breathDataLast() {
      return this.breathData[this.breathData.length - 1]
    },
    stepsDataLast() {
      return this.stepsData[this.stepsData.length - 1]
    },
    hrLength() {
      return this.rhythmogram.timeValues.length
    },
    brLength() {
      return this.breath.timeValues.length
    },
    stepLength() {
      return this.steps.timeValues.length
    },
    hasBreath() {
      return this.breath && this.breath.brValues.length > 0
    },
    hasStepRates() {
      return this.steps && this.steps.rateValues.length > 0
    },
    trainingId() {
      return this.$route.params.id
    },
    eventsLength() {
      if (this.hasEvents) return `${this.events.length}`
      else return ''
    }
  },
  watch: {
    minHRValue() {
      this.valueAxis.min = this.minHRValue
    },
    maxHRValue() {
      this.valueAxis.max = this.maxHRValue
    },
    minBreathValue() {
      this.valueBreathAxis.min = this.minBreathValue
    },
    maxBreathValue() {
      this.valueBreathAxis.max = this.maxBreathValue
    },
    duration() {
      this.durationAxis.max = this.duration
    },
    hasEvents() {
      if (this.hasEvents) {
        this.setEventRanges()
      }
    },
    events: {
      deep: true,
      handler(newVal, oldVal) {
        this.emptySeries.legendSettings.labelText = `${this.$t('sessionsInfo.events')} \xa0\xa0 ${
          this.eventsLength
        }`
        if (!this.isActiveTraining) {
          this.filterEventRanges()
          return
        }

        let diff = newVal.length
        if (oldVal) {
          diff = newVal.length - oldVal.length
        }

        if (diff > 0) {
          // если изменения касаются добавления события
          for (let i = 1; i <= diff; i++) {
            this.createEventRange(newVal[newVal.length - 1])
          }
        } else {
          //если изменения внутри событий то надо найти различия и изменить range
          const updatedEventIndex = newVal.findIndex((event, i) => {
            return oldVal[i]['end'] !== event['end']
          })
          if (updatedEventIndex) {
            this.updateEventRange(updatedEventIndex)
          }
        }
      }
    },
    rhythmogram: {
      deep: true,
      handler() {
        debugger
        this.chart.addData({
          duration: this.rhythmogram.timeValues[this.hrLength - 1],
          hr: this.rhythmogram.hrValues[this.hrLength - 1],
          durationTime: new Date(
            this.dateStart.getTime() + this.rhythmogram.timeValues[this.hrLength - 1] * 1000
          )
        })
      }
    },
    breath: {
      deep: true,
      handler() {
        this.breathSeries.addData({
          duration: this.breath.timeValues[this.brLength - 1],
          breath: this.breath.brValues[this.brLength - 1],
          durationTime: new Date(this.dateStart.getTime() + this.breath.timeValues[this.brLength - 1] * 1000)
        })
      }
    },
    steps: {
      deep: true,
      handler() {
        this.stepSeries.addData({
          duration: this.steps.timeValues[this.stepLength - 1],
          step: this.steps.rateValues[this.stepLength - 1],
          durationTime: new Date(this.dateStart.getTime() + this.steps.timeValues[this.stepLength - 1] * 1000)
        })
      }
    },
    chartData: {
      deep: true,
      handler() {
        if (!this.chartDataLast) {
          return
        }
        if (this.chartDataLast.hr < this.minHRValue) {
          this.minHRValue = this.chartDataLast.hr
        } else if (this.chartDataLast.hr > this.maxHRValue) {
          this.maxHRValue = this.chartDataLast.hr
        }

        if (this.isActiveTraining && this.chartDataLast.duration > this.duration) {
          this.duration += DEFAULT_DURATION_SEC
        }
      }
    },
    breathData: {
      deep: true,
      handler() {
        if (!this.breathDataLast) {
          return
        }
        if (this.breathDataLast.breath < this.minBreathValue) {
          this.minBreathValue = this.breathDataLast.breath
        } else if (this.breathDataLast.breath > this.maxBreathValue) {
          this.maxBreathValue = this.breathDataLast.breath
        }

        if (this.isActiveTraining && this.breathDataLast.duration > this.duration) {
          this.duration += DEFAULT_DURATION_SEC
        }
      }
    },
    stepsData: {
      deep: true,
      handler() {
        if (!this.stepsDataLast) {
          return
        }
        if (this.stepsDataLast.step < this.minHRValue) {
          this.minHRValue = this.stepsDataLast.step
        } else if (this.stepsDataLast.step > this.maxHRValue) {
          this.maxHRValue = this.stepsDataLast.step
        }

        if (this.isActiveTraining && this.stepsDataLast.duration > this.duration) {
          this.duration += DEFAULT_DURATION_SEC
        }
      }
    },
    ecgRange: {
      deep: true,
      handler() {
        this.updateValueForECGAxisRange(this.ecgRange.start, this.ecgRange.end)
      }
    },
    trainingType() {
      this.$forceUpdate()
    }
  },
  mounted() {
    this.initializeComponent()
  },
  updated() {
    this.initializeComponent()
  },
  beforeDestroy() {
    this.disposeChart()
  },

  methods: {
    initializeComponent() {
      this.disposeChart()

      am4core.useTheme(amThemeAnimated)
      this.createChart()
      this.showIndicator()
      if (!this.isActiveTraining) {
        this.duration = this.trainingDuration
      }
      if (this.min) {
        this.minHRValue = this.min
      }
      if (this.max) {
        this.maxHRValue = this.max
      }
      this.setChartData()
      this.setBreathData()
      this.setStepData()
      this.createGraph()

      if (this.hasEvents) {
        this.setEventRanges()
      }
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
        this.durationAxis = null
        this.ecgAxisRange = null
      }
    },
    setChartData() {
      let data = []

      for (let i = 0; i < this.hrLength; i++) {
        const time = Math.round(this.rhythmogram.timeValues[i])
        const hrValue = Math.round(this.rhythmogram.hrValues[i])
        const dataItem = {
          duration: time,
          durationTime: new Date(this.dateStart.getTime() + time * 1000),
          hr: hrValue
        }

        if (this.steps) {
          const stepIdx = this.steps.timeValues.indexOf(time)
          if (stepIdx !== -1) {
            dataItem.step = this.steps.rateValues[stepIdx]
          }
        }

        if (hrValue < this.minHRValue) {
          this.minHRValue = hrValue
        } else if (hrValue > this.maxHRValue) {
          this.maxHRValue = hrValue
        }

        if (dataItem.step < this.minHRValue) {
          this.minHRValue = dataItem.step
        }
        if (dataItem.step > this.maxHRValue) {
          this.maxHRValue = dataItem.step
        }

        data.push(dataItem)
      }

      this.chartData = data
    },
    setBreathData() {
      if (!this.hasBreath) {
        return
      }
      const data = []
      this.breath.timeValues.forEach((time, index) => {
        const breath = this.breath.brValues[index]
        if (breath > this.maxBreathValue) {
          this.maxBreathValue = breath
        } else if (breath < this.minBreathValue) {
          this.minBreathValue = breath
        }
        data.push({
          duration: time,
          breath: breath,
          durationTime: new Date(this.dateStart.getTime() + time * 1000)
        })
        if (time > this.duration) {
          this.duration = time
        }
      })
      this.breathData = data
    },
    setStepData() {
      if (!this.hasStepRates) {
        return
      }

      let data = []
      this.steps.timeValues.forEach((time, index) => {
        const steps = this.steps.rateValues[index]
        if (steps > this.maxHRValue) {
          this.maxHRValue = steps
        } else if (steps < this.minHRValue) {
          this.minHRValue = steps
        }
        data.push({
          duration: time,
          step: steps,
          durationTime: new Date(this.dateStart.getTime() + time * 1000)
        })
        if (time > this.duration) {
          this.duration = time
        }
      })
      this.stepsData = data
    },
    createChart() {
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)
      this.indicator = this.chart.tooltipContainer.createChild(am4core.Container)
      this.indicator.background.fill = am4core.color('#fff')
      this.indicator.background.fillOpacity = 0.8
      this.indicator.width = am4core.percent(100)
      this.indicator.height = am4core.percent(100)
      this.indicatorLabel = this.indicator.createChild(am4core.Label)
      ;(this.indicatorLabel.text = this.$t('src/base/basespinner.9012')),
        (this.indicatorLabel.align = 'center')
      this.indicatorLabel.valign = 'middle'
      this.indicatorLabel.fontSize = 20
    },
    createGraph() {
      while (this.chart.series.length > 0) {
        this.chart.series.removeIndex(0)
      }
      while (this.chart.yAxes.length > 0) {
        this.chart.yAxes.removeIndex(0)
      }
      while (this.chart.xAxes.length > 0) {
        this.chart.xAxes.removeIndex(0)
      }

      this.chart.data = this.chartData
      this.chart.dataSource.updateCurrentData = true
      this.chart.colors.list = [am4core.color('#ff7766'), am4core.color('#aaaaaa'), am4core.color('#1b93ea')]

      // Создаем ось X в зависимости от режима
      if (this.isTimeMode) {
        this.createDateAxis()
      } else {
        this.createDurationAxis()
      }

      //y
      this.valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
      this.valueAxis.renderer.minGridDistance = 30
      this.valueAxis.min = this.minHRValue
      this.valueAxis.max = this.maxHRValue
      this.valueAxis.title.text = `${this.$t('sessionsInfo.heartRate')}, ${this.$t(
        'sessionsInfo.walkingPace'
      )}`
      this.valueAxis.title.fontSize = '12px'
      this.valueAxis.title.fontFamily = 'Arial'

      this.hrSeries = this.chart.series.push(new am4charts.LineSeries())
      this.hrSeries.dataFields.valueY = 'hr'
      this.hrSeries.id = 'hrSeries'
      this.hrSeries.name = this.$t('sessionsInfo.heartRate')
      this.hrSeries.numberFormatter.numberFormat = '#.'

      this.stepSeries = this.chart.series.push(new am4charts.LineSeries())
      this.stepSeries.dataFields.valueY = 'step'
      this.stepSeries.id = 'stepSeries'
      this.stepSeries.name = this.$t('sessionsInfo.walkingPace')
      this.stepSeries.data = this.stepsData

      this.valueBreathAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
      this.valueBreathAxis.renderer.minGridDistance = 30
      this.valueBreathAxis.min = this.minBreathValue
      this.valueBreathAxis.max = this.maxBreathValue
      this.valueBreathAxis.title.text = this.$t('sessionsInfo.respiratoryRate')
      this.valueBreathAxis.title.fontSize = '12px'
      this.valueBreathAxis.title.fontFamily = 'Arial'
      this.valueBreathAxis.renderer.opposite = true
      this.valueBreathAxis.title.rotation = 270
      this.valueBreathAxis.renderer.grid.template.disabled = true

      this.breathSeries = this.chart.series.push(new am4charts.LineSeries())
      this.breathSeries.dataFields.valueY = 'breath'
      this.breathSeries.id = 'breathSeries'
      this.breathSeries.name = this.$t('sessionsInfo.breathingRate')
      this.breathSeries.yAxis = this.valueBreathAxis
      this.breathSeries.data = this.breathData

      this.emptySeries = this.chart.series.push(new am4charts.LineSeries())
      this.emptySeries.strokeWidth = 0
      this.emptySeries.id = 'emptySeries'
      this.emptySeries.dataFields.valueY = 'value'
      this.emptySeries.data = [
        {
          duration: 0,
          value: this.minHRValue + 10,
          durationTime: new Date(this.dateStart.getTime())
        },
        {
          duration: this.duration,
          value: this.minHRValue + 10,
          durationTime: new Date(this.dateStart.getTime() + this.duration * 1000)
        }
      ]

      if (this.isTimeMode) {
        this.hrSeries.dataFields.dateX = 'durationTime'
        this.stepSeries.dataFields.dateX = 'durationTime'
        this.breathSeries.dataFields.dateX = 'durationTime'
        this.emptySeries.dataFields.dateX = 'durationTime'
      } else {
        this.hrSeries.dataFields.valueX = 'duration'
        this.stepSeries.dataFields.valueX = 'duration'
        this.breathSeries.dataFields.valueX = 'duration'
        this.emptySeries.dataFields.valueX = 'duration'
      }

      this.chart.legend = new am4charts.Legend()
      this.chart.legend.valueLabels.template.align = 'left'
      this.chart.legend.valueLabels.template.textAlign = 'start'

      this.hrSeries.legendSettings.itemValueText = '{valueY.close}'
      this.stepSeries.legendSettings.itemValueText = '{valueY.close.}'
      this.breathSeries.legendSettings.itemValueText = '{valueY.close}'
      this.emptySeries.legendSettings.labelText = `${this.$t('sessionsInfo.events')} \xa0 ${
        this.eventsLength
      }`

      if (this.isActiveTraining) {
        this.hrSeries.legendSettings.valueText = '{valueY.close}'
        this.stepSeries.legendSettings.valueText = '{valueY.close}'
        this.breathSeries.legendSettings.valueText = '{valueY.close}'
      }

      this.chart.durationFormatter.durationFormat = this.duration < 3600 ? 'mm:ss' : 'hh:mm:ss'

      this.createCursor()

      this.createECGAxisRange()
      this.updateValueForECGAxisRange(this.ecgRange.start, this.ecgRange.end)

      if (this.min) {
        this.createYRanges(this.min, this.$t('src/components/training/trainingchart.min'))
      }
      if (this.max) {
        this.createYRanges(this.max, this.$t('src/components/training/trainingchart.max'))
      }
      if (this.patientMaxHr) {
        this.createYRanges(
          this.patientMaxHr,
          this.$t('src/components/training/trainingchart.hrMax'),
          '#F44336'
        )
      }
      if (this.trainingType) this.createRangeSlider()

      this.chart.plotContainer.events.on(
        'hit',
        () => {
          if (this.clickOnZoomOutButton) {
            this.clickOnZoomOutButton = false
            return
          }
          if (!this.$parent.$refs.ecg1.loading && !this.$parent.$refs.ecg1.drawing)
            this.$emit('input', this.cursorPosition.x)
        },
        this
      )

      this.chart.zoomOutButton.events.on('hit', () => {
        this.clickOnZoomOutButton = true
        const heartRate = getAverageOfArray(this.rhythmogram.hrValues)
        const breath = this.breath ? getAverageOfArray(this.breath.brValues) : null
        this.$emit('set:heartRate', heartRate, breath)
      })

      this.chart.events.on(
        'ready',
        function() {
          this.hideIndicator()
          if (this.trainingType) {
            this.setRange()
          }
        },
        this
      )

      this.durationAxis.events.on(
        'rangechangeended',
        ev => {
          const ecgComponent = this.$parent.$refs.ecg1
          if (ecgComponent) {
            let cursorPosition

            if (this.isTimeMode) {
              const startSeconds = (ev.target.start - this.dateStart.getTime()) / 1000
              cursorPosition = startSeconds + ecgComponent.numberOfSeconds / 2
            } else {
              cursorPosition = ev.target.start + ecgComponent.numberOfSeconds / 2
            }

            if (!ecgComponent.loading && !ecgComponent.drawing && ev.target.zoomFactor !== 1)
              this.$emit('input', cursorPosition)
          }
        },
        this
      )

      this.chart.logo.disabled = true
    },
    findAverage(ev) {
      const range = ev.target.xRange
      const axis = ev.target.chart.xAxes.getIndex(0)
      const from = axis.getPositionLabel(axis.toAxisPosition(range.start))
      const to = axis.getPositionLabel(axis.toAxisPosition(range.end))
      const heartRate = this.findAverageSelectedGraph(
        this.rhythmogram.timeValues,
        this.rhythmogram.hrValues,
        +from,
        +to
      )
      const breath = this.breath
        ? this.findAverageSelectedGraph(this.breath.timeValues, this.breath.brValues, +from, +to)
        : null

      this.$emit('set:heartRate', heartRate, breath)
    },
    createCursor() {
      this.throttled = false
      this.chart.cursor = new am4charts.XYCursor()
      this.chart.cursor.behavior = 'zoomX'
      this.chart.cursor.maxTooltipDistance = 3
      this.chart.cursor.dateAxis = this.durationAxis

      this.last_idx = -1
      this.isDragged = false

      this.chart.cursor.events.on('cursorpositionchanged', ev => {
        const xAxis = ev.target.chart.xAxes.getIndex(0)
        const rawValue = xAxis.positionToValue(xAxis.toAxisPosition(ev.target.xPosition))

        if (this.isTimeMode) {
          this.cursorPosition.x = (rawValue - this.dateStart.getTime()) / 1000
        } else {
          this.cursorPosition.x = rawValue
        }
        if (this.throttled) {
          this.target = ev.target
          return
        }

        this.throttled = true
        this.setLegendForPosition(ev.target)

        setTimeout(() => {
          this.throttled = false
          if (this.target) {
            this.setLegendForPosition(this.target)
            this.target = null
          }
        }, 200)
      })

      this.chart.cursor.events.on(
        'zoomended',
        ev => {
          this.findAverage(ev)
        },
        this
      )
    },
    setLegendForPosition(target) {
      const xAxis = target.chart.xAxes.getIndex(0)
      const rawValue = xAxis.positionToValue(xAxis.toAxisPosition(target.xPosition))
      const xValue = this.isTimeMode ? (rawValue - this.dateStart.getTime()) / 1000 : rawValue

      const idx = this.searchval(xValue)
      if (idx !== this.last_idx) {
        const dataItem = this.chart.data[idx]
        this.hrSeries.legendSettings.itemValueText = dataItem.hr

        if (dataItem.step || dataItem.step === 0) {
          this.stepSeries.legendSettings.itemValueText = dataItem.step
        }
        if (this.hasBreath) {
          const duration = dataItem.duration
          const nearestBreath = this.breathSeries.data.find(
            breath => Math.abs(breath.duration - duration) < 4
          )
          this.breathSeries.legendSettings.itemValueText = nearestBreath
            ? Math.round(nearestBreath.breath)
            : null
        }
      }
    },
    createYRanges(value, name, fill = '#007bff') {
      const axisRange = this.valueAxis.axisRanges.create()
      const config = {
        value: value,
        grid: {
          stroke: fill,
          strokeWidth: 1,
          strokeOpacity: 0.8,
          strokeDasharray: 3
        },
        label: {
          text: name,
          inside: false,
          align: 'right',
          verticalCenter: 'middle',
          fill: fill,
          fontSize: '0.65rem',
          dx: -25
        }
      }
      axisRange.config = config
    },
    createECGAxisRange() {
      this.ecgAxisRange = this.durationAxis.axisRanges.create()
      this.ecgAxisRange.axisFill.fill = am4core.color('#f76')
      this.ecgAxisRange.axisFill.fillOpacity = 0.2
    },
    updateValueForECGAxisRange(value = 0, endValue = 0) {
      if (!this.ecgAxisRange) return

      if (this.isTimeMode) {
        this.ecgAxisRange.value = new Date(this.dateStart.getTime() + value * 1000)
        this.ecgAxisRange.endValue = new Date(this.dateStart.getTime() + endValue * 1000)
      } else {
        this.ecgAxisRange.value = value
        this.ecgAxisRange.endValue = endValue
      }
    },
    setEventRanges() {
      this.events.forEach(event => {
        this.createEventRange(event)
      })
    },
    createEventRange(event) {
      if (event.type == 'LostSamples') return
      let axisRange
      const colors = {
        warning: '#FAAD14',
        secondary: '#6c757d',
        primary: '#0482dc'
      }
      const color = colors[event.color] || '#F5222D'

      if (event.isDuration) {
        axisRange = this.durationAxis.createSeriesRange(this.emptySeries)
        if (this.isTimeMode) {
          axisRange.value = new Date(this.dateStart.getTime() + event.begin * 1000)
          axisRange.endValue = new Date(this.dateStart.getTime() + event.end * 1000)
        } else {
          axisRange.value = event.begin
          axisRange.endValue = event.end
        }
        axisRange.contents.stroke = am4core.color(color)
        axisRange.contents.fill = axisRange.contents.stroke
        axisRange.contents.fillOpacity = 0.3
        axisRange.config = {
          axisFill: {
            tooltip: {
              type: 'Tooltip',
              fitPointerToBounds: true,
              getFillFromObject: false,
              background: {
                fillOpacity: 0,
                cornerRadius: 0,
                strokeWidth: 0,
                pointerLength: 0
              },
              label: {
                fill: color
              },
              height: 100,
              dy: -10,
              horizontalCenter: 'middle'
            },
            width: '100%',
            tooltipText: event.title + ' (' + timeDuration(event.begin, event.end) + ')',
            interactionsEnabled: true
          }
        }
      } else if (event.abbr !== 'RC') {
        axisRange = this.durationAxis.axisRanges.create()
        axisRange.value = this.isTimeMode
          ? new Date(this.dateStart.getTime() + event.begin * 1000)
          : event.begin
        axisRange.grid.disabled = true
        axisRange.bullet = new am4core.Triangle()
        axisRange.bullet.width = 10
        axisRange.bullet.height = 6
        axisRange.bullet.fill = am4core.color(color)
        if (event.value) {
          axisRange.bullet.tooltipText = event.unit
            ? `${event.title} ${event.value}${event.unit}`
            : `${event.title} ${event.value}`
        } else {
          axisRange.bullet.tooltipText = event.title
        }
      }

      this.axisRanges[event.key] = axisRange
    },
    updateEventRange(idx) {
      if (!this.events[idx]) return

      const range = this.axisRanges[this.events[idx].key]
      if (!range) return

      if (this.events[idx].isDuration) {
        if (this.isTimeMode) {
          range.endValue = new Date(this.dateStart.getTime() + this.events[idx].end * 1000)
        } else {
          range.endValue = this.events[idx].end
        }
      }
    },
    filterEventRanges() {
      // eslint-disable-next-line no-unused-vars
      for (let key in this.axisRanges) {
        const range = this.axisRanges[key]
        if (!range) continue

        const shouldBeVisible = this.events.some(event => event.key === key)

        range.opacity = shouldBeVisible ? 1 : 0

        if (!range.bullet && range.axisFill && range.axisFill.tooltip) {
          range.axisFill.tooltip.label.opacity = shouldBeVisible ? 1 : 0
        }
      }
    },
    searchval(x) {
      let left = 0
      let right = this.chart.data.length - 1

      while (left < right) {
        const center = Math.floor((left + right) / 2)
        if (this.chart.data[center].duration < x) {
          left = center + 1
        } else {
          right = center
        }
      }

      if (left < this.chart.data.length - 1) {
        const diff1 = Math.abs(x - this.chart.data[left].duration)
        const diff2 = Math.abs(x - this.chart.data[left + 1].duration)
        if (diff2 < diff1) {
          left += 1
        }
      }

      return left
    },

    findAverageSelectedGraph(timeValues, values, from, to) {
      if (this.isTimeMode) {
        from = (from - this.dateStart.getTime()) / 1000
        to = (to - this.dateStart.getTime()) / 1000
      }

      let fromClosest = null
      let toClosest = null
      let fromClosestIdx = null
      let toClosestIdx = null

      timeValues.forEach((item, i) => {
        if (fromClosest == null || Math.abs(item - from) < Math.abs(fromClosest - from)) {
          fromClosest = item
          fromClosestIdx = i
        } else if (toClosest == null || Math.abs(item - to) < Math.abs(toClosest - to)) {
          toClosest = item
          toClosestIdx = i
        }
      })

      let shortArray = values.slice(fromClosestIdx, toClosestIdx + 1)
      return getAverageOfArray(shortArray)
    },
    createRangeSlider() {
      this.range = this.durationAxis.axisRanges.create()
      this.range.value = window.sessionStorage.getItem(`markStart${this.trainingId}`)
      this.range.grid.stroke = am4core.color('#5fa5c5')
      this.range.grid.strokeWidth = 2
      this.range.grid.strokeOpacity = 5
      this.range.bullet = new am4core.ResizeButton()
      this.range.bullet.background.fill = am4core.color('#5fa5c5')
      this.range.bullet.background.states.copyFrom(this.chart.zoomOutButton.background.states)
      this.range.bullet.minX = 0

      const chart = this.chart
      this.range.bullet.adapter.add('minY', function(minY, target) {
        target.maxY = 0
        target.maxX = chart.plotContainer.maxWidth
        return 0
      })

      this.range.bullet.events.on(
        'dragged',
        function() {
          this.range.value = this.durationAxis.xToValue(this.range.bullet.pixelX)
          this.chart.cursor.behavior = 'none'
        },
        this
      )

      this.range.bullet.events.on(
        'dragstop',
        function() {
          window.sessionStorage.setItem(`markStart${this.trainingId}`, this.range.value)
          setTimeout(() => {
            this.chart.cursor.behavior = 'zoomX'
          }, 1000)
        },
        this
      )
      this.rangeEnd = this.durationAxis.axisRanges.create()
      this.rangeEnd.value = window.sessionStorage.getItem(`markEnd${this.trainingId}`)
      this.rangeEnd.grid.stroke = am4core.color('#5fa5c5')
      this.rangeEnd.grid.strokeWidth = 2
      this.rangeEnd.grid.strokeOpacity = 5
      this.rangeEnd.bullet = new am4core.ResizeButton()
      this.rangeEnd.bullet.background.fill = am4core.color('#5fa5c5')
      this.rangeEnd.bullet.background.states.copyFrom(this.chart.zoomOutButton.background.states)
      this.rangeEnd.bullet.minX = 0
      this.rangeEnd.bullet.maxY = 0
      this.rangeEnd.bullet.adapter.add('minY', function(minY, target) {
        target.maxY = 0
        target.maxX = chart.plotContainer.maxWidth
        return 0
      })

      this.rangeEnd.bullet.events.on(
        'dragged',
        function() {
          this.rangeEnd.value = this.durationAxis.xToValue(this.rangeEnd.bullet.pixelX)
          this.chart.cursor.behavior = 'none'
        },
        this
      )

      this.rangeEnd.bullet.events.on(
        'dragstop',
        function() {
          window.sessionStorage.setItem(`markEnd${this.trainingId}`, this.rangeEnd.value)
          setTimeout(() => {
            this.chart.cursor.behavior = 'zoomX'
          }, 1000)
        },
        this
      )
    },
    setRange() {
      const range = this.range
      const rangeEnd = this.rangeEnd
      const xAxis = this.chart.xAxes.getIndex(0)
      const position = xAxis.valueToPoint(360)
      this.rangeEnd.bullet.adapter.add(
        'minY',
        function(minY, target) {
          target.minX = range.bullet.pixelX
          target.maxX = range.bullet.pixelX + position.x
          return 0
        },
        this
      )

      this.range.bullet.adapter.add(
        'minY',
        function(minY, target) {
          target.minX = rangeEnd.bullet.pixelX - position.x < 0 ? 0 : rangeEnd.bullet.pixelX - position.x
          target.maxX = rangeEnd.bullet.pixelX
          return 0
        },
        this
      )
    },
    showIndicator() {
      if (this.indicator) {
        this.indicator.show()
      }
    },
    hideIndicator() {
      this.indicator.hide()
    },
    createDurationAxis() {
      this.durationAxis = this.chart.xAxes.push(new am4charts.DurationAxis())

      this.durationAxis.renderer.minGridDistance = 100
      this.durationAxis.title.text = this.$t('src/components/training/trainingchart.timeSessionStart')

      this.durationAxis.fontSize = '0.875rem'
      this.durationAxis.title.fontSize = '0.875rem'
      this.durationAxis.title.valign = 'bottom'
      this.durationAxis.renderer.grid.template.location = 0
      this.durationAxis.min = 0
      this.durationAxis.max = this.duration
      this.durationAxis.strictMinMax = true
      this.durationAxis.baseUnit = 'second'
      this.durationAxis.tooltip.label.opacity = 1

      this.durationAxis.title.fill = am4core.color('#3c78d8')
      this.durationAxis.title.interactionsEnabled = true
      this.durationAxis.title.hoverState = this.durationAxis.title.states.create('hover')
      this.durationAxis.title.cursorOverStyle = am4core.MouseCursorStyle.pointer
      this.durationAxis.title.hoverState.properties.fill = am4core.color('#1a4580')

      this.durationAxis.title.events.on('hit', () => {
        this.toggleXAxisType()
      })
    },
    createDateAxis() {
      this.durationAxis = this.chart.xAxes.push(new am4charts.DateAxis())

      this.durationAxis.renderer.minGridDistance = 100
      this.durationAxis.title.text = this.$t('src/components/training/trainingchart.absoluteTime')

      this.durationAxis.fontSize = '0.875rem'
      this.durationAxis.title.fontSize = '0.875rem'
      this.durationAxis.title.valign = 'bottom'
      this.durationAxis.renderer.grid.template.location = 0

      this.durationAxis.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm:ss'
      this.durationAxis.dateFormatter.dateFormat = 'HH:mm:ss'
      this.durationAxis.periodChangeDateFormats.setKey('second', 'HH:mm:ss')
      this.durationAxis.periodChangeDateFormats.setKey('minute', 'HH:mm')
      this.durationAxis.periodChangeDateFormats.setKey('hour', 'HH:mm')
      this.durationAxis.periodChangeDateFormats.setKey('day', 'MMM dd')

      this.durationAxis.min = new Date(this.dateStart)
      this.durationAxis.max = new Date(this.dateStart.getTime() + this.duration * 1000)

      this.durationAxis.renderer.labels.template.disabled = false
      this.durationAxis.renderer.grid.template.disabled = false

      this.durationAxis.title.fill = am4core.color('#3c78d8')
      this.durationAxis.title.interactionsEnabled = true
      this.durationAxis.title.cursorOverStyle = am4core.MouseCursorStyle.pointer
      this.durationAxis.title.hoverState = this.durationAxis.title.states.create('hover')
      this.durationAxis.title.hoverState.properties.fill = am4core.color('#1a4580')
      this.durationAxis.title.events.on('hit', () => {
        this.toggleXAxisType()
      })
    },
    toggleXAxisType() {
      this.isTimeMode = !this.isTimeMode
      this.initializeComponent()
    }
  }
}
</script>
